"use client";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

// Coverage helpers (match PODetailView3 / MaterialsTab3)
const COVERAGE_UOMS = [
  "sf",
  "lf",
  "sq ft",
  "lin ft",
  "square",
  "linear",
  "sheet",
];
const isCoverageUom = (uom) =>
  COVERAGE_UOMS.some((u) => (uom || "").toLowerCase().includes(u));
const isSF = (uom) =>
  ["sf", "sq ft", "square"].some((u) => (uom || "").toLowerCase().includes(u));
const isLF = (uom) =>
  ["lf", "lin ft", "linear"].some((u) => (uom || "").toLowerCase().includes(u));
const covLabel = (uom) =>
  isSF(uom) ? "SF" : isLF(uom) ? "LF" : (uom || "").toUpperCase();

// Group everything by item code only. Size varies (and so does cost), so we
// compare on total COVERAGE (SF/LF) and cost rather than PC counts, which
// aren't additive across sizes.
const codeKey = (code) => (code || "").trim().toLowerCase();

// Compact inline progress bar used per-row (qty-based, secondary info)
function QtyBar({ delivered, ordered, estimated }) {
  const target = estimated > 0 ? estimated : ordered;
  const ordPct = target > 0 ? Math.min(100, (ordered / target) * 100) : 0;
  const delPct = target > 0 ? Math.min(100, (delivered / target) * 100) : 0;
  const over = ordered > target && target > 0;
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="relative flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full",
            over ? "bg-red-500/40" : "bg-yellow-600/40",
          )}
          style={{ width: `${ordPct}%` }}
        />
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full transition-all",
            delPct >= 100 ? "bg-green-500" : "bg-orange-500",
          )}
          style={{ width: `${delPct}%` }}
        />
      </div>
      <span className="text-[10px] text-gray-400 shrink-0 font-mono whitespace-nowrap">
        {fmtQty(ordered)}/{fmtQty(target)}
      </span>
    </div>
  );
}

// Big hero bar for the overall $ budget-remaining figure at the top of the panel
function BudgetHeroBar({ budget, spent }) {
  const pctSpent = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  const remaining = budget - spent;
  const over = remaining < 0;
  return (
    <div className="relative bg-gray-900 rounded-full h-3 overflow-hidden border border-gray-700">
      <div
        className={cls(
          "absolute left-0 top-0 h-full rounded-full transition-all",
          over
            ? "bg-red-500"
            : pctSpent > 85
              ? "bg-yellow-500"
              : "bg-emerald-500",
        )}
        style={{ width: `${pctSpent}%` }}
      />
    </div>
  );
}

// =============================================================================
// BudgetImpactPanel - one row per item CODE on this order/request.
// Compares total coverage (SF/LF) and cost against budget, since size/cost
// vary per order and would otherwise fragment a single budgeted item.
//
// Budgeted vs off-budget items are kept fully separate: the summary cards and
// "Budget Remaining" reflect ONLY budget-tracked codes. Unbudgeted codes get
// their own section + subtotal so they never distort the budget math.
//
// Props:
//   poId        - PurchaseOrder3 _id this order/request is tied to
//   token       - bearer token (falls back to localStorage)
//   orderRows   - normalized current lines (ONLY these codes are shown):
//                 [{ itemCode, size, uom, quantity, unitPrice, coveragePerUnit }]
//                 quantity = PC, unitPrice = $/PC
//   refreshKey  - bump to force a refetch (e.g. order.updatedAt)
//   title       - optional heading override
// =============================================================================
export default function BudgetImpactPanel({
  poId,
  token,
  orderRows = [],
  refreshKey,
  title = "Budget Impact",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [extraDescriptions, setExtraDescriptions] = useState({}); // itemCode → description
  const [showTable, setShowTable] = useState(true);

  useEffect(() => {
    if (!poId) {
      setItems([]);
      setLoading(false);
      return;
    }
    const auth = token || localStorage.getItem("token");
    setLoading(true);
    setError("");
    axios
      .get(`${API}/purchaseOrders3/${poId}/items?limit=1000`, {
        headers: { Authorization: `Bearer ${auth}` },
      })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [poId, token, refreshKey]);

  const descriptionByCode = useMemo(() => {
    const m = new Map();
    // From budget items first
    for (const i of items) {
      const k = codeKey(i.itemCode);
      const desc =
        i.costCodeDescription || i.itemDescription || i.description || "";
      if (desc && !m.get(k)) m.set(k, desc);
    }
    // From orderRows
    for (const li of orderRows) {
      const k = codeKey(li.itemCode);
      if (li.description && !m.get(k)) m.set(k, li.description);
    }
    // From ItemDatabase lookup (for off-budget items with no description anywhere)
    for (const [code, desc] of Object.entries(extraDescriptions)) {
      const k = codeKey(code);
      if (desc && !m.get(k)) m.set(k, desc);
    }
    return m;
  }, [items, orderRows, extraDescriptions]);

  // Stable primitive key of codes still missing a description, so the effect
  // only re-runs when the actual set of missing codes changes — not on every
  // render (descriptionByCode is a fresh Map each time and would loop).
  const missingCodesKey = useMemo(() => {
    if (loading || !orderRows.length) return "";
    const missing = orderRows
      .filter((li) => {
        const k = codeKey(li.itemCode);
        return li.itemCode && !li.description && !descriptionByCode.get(k);
      })
      .map((li) => (li.itemCode || "").trim())
      .filter(Boolean);
    return [...new Set(missing)].sort().join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, orderRows, descriptionByCode]);

  useEffect(() => {
    if (!missingCodesKey) return;
    const missingCodes = missingCodesKey.split("|");

    const auth = token || localStorage.getItem("token");
    axios
      .post(
        `${API}/materialOrders3/items/descriptions`,
        { ids: [], itemCodes: missingCodes },
        { headers: { Authorization: `Bearer ${auth}` } },
      )
      .then((r) => {
        const { byCode = {} } = r.data;
        // Only update state if we actually got new descriptions, otherwise
        // we retrigger descriptionByCode → this effect → loop.
        setExtraDescriptions((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const [code, desc] of Object.entries(byCode)) {
            if (desc && next[code] !== desc) {
              next[code] = desc;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      })
      .catch(() => {});
  }, [missingCodesKey, token]);

  // This order/request aggregated by item code.
  // Coverage items -> quantity in SF/LF; otherwise PC.
  const orderByCode = useMemo(() => {
    const m = new Map();
    for (const li of orderRows) {
      const qtyPC = parseFloat(li.quantity) || 0;
      if (qtyPC <= 0) continue;
      const cov = isCoverageUom(li.uom)
        ? parseFloat(li.coveragePerUnit) || 1
        : 1;
      const isCov = isCoverageUom(li.uom);
      const k = codeKey(li.itemCode);

      const prev = m.get(k) || {
        itemCode: li.itemCode,
        description: li.description || "",
        isCov,
        uom: li.uom,
        qty: 0,
        amount: 0,
        sizes: new Set(),
      };
      if (isCov) {
        prev.isCov = true;
        prev.uom = li.uom;
      }
      prev.qty += isCov ? qtyPC * cov : qtyPC;
      prev.amount += qtyPC * (parseFloat(li.unitPrice) || 0);
      if (li.size) prev.sizes.add(String(li.size).trim());
      if (li.description && !prev.description)
        prev.description = li.description;

      m.set(k, prev);
    }
    return m;
  }, [orderRows]);

  // Budget + CO + manual rolled up by item code, in coverage/cost terms.
  const budgetByCode = useMemo(() => {
    const m = new Map();
    const add = (line, covMult) => {
      const k = codeKey(line.itemCode);
      const isCov = isCoverageUom(line.uom || line.container);
      const cov = isCov ? parseFloat(line.coveragePerUnit) || 1 : 1;
      const prev = m.get(k) || {
        itemCode: line.itemCode,
        description: line.description || "",
        isCov: false,
        uom: line.uom || line.container,
        budgetQty: 0,
        budgetCost: 0,
        orderedQty: 0,
        orderedAmount: 0,
        deliveredQty: 0,
        deliveredAmount: 0,
        coDeltaQty: 0,
      };
      if (isCov) {
        prev.isCov = true;
        prev.uom = line.uom || line.container;
      }
      if (!prev.description && line.description)
        prev.description = line.description;

      const mult = isCov ? cov : 1;
      prev.budgetQty += (line.netQty || 0) * mult * covMult.budget;
      prev.budgetCost += (line.netCost || 0) * covMult.budget;
      prev.orderedQty += (line.qtyOrdered || 0) * mult;
      prev.orderedAmount += line.amountOrdered || 0;
      prev.deliveredQty += (line.qtyDelivered || 0) * mult;
      prev.deliveredAmount += line.amountDelivered || 0;
      prev.coDeltaQty += (line.coDeltaQty || 0) * mult;
      m.set(k, prev);
    };

    for (const i of items) {
      if (i.source === "budget") {
        add(
          {
            itemCode: i.itemCode,
            description:
              i.costCodeDescription ||
              i.itemDescription ||
              i.description ||
              i.name ||
              "",
            uom: i.uom || i.container,
            coveragePerUnit: i.coveragePerUnit,
            netQty: i.requiredQty || 0,
            netCost: i.amount || 0,
            qtyOrdered: i.qtyOrdered || 0,
            amountOrdered: i.amountOrdered || 0,
            qtyDelivered: i.qtyDelivered || 0,
            amountDelivered: i.amountDelivered || 0,
            coDeltaQty: 0,
          },
          { budget: 1 },
        );
      } else if (i.source === "change_order") {
        add(
          {
            itemCode: i.itemCode,
            description:
              i.costCodeDescription ||
              i.itemDescription ||
              i.description ||
              i.name ||
              "",
            uom: i.uom || i.container,
            coveragePerUnit: i.coveragePerUnit,
            netQty: i.deltaQty || 0,
            netCost: i.amount || 0,
            qtyOrdered: i.qtyOrdered || 0,
            amountOrdered: i.amountOrdered || 0,
            qtyDelivered: i.qtyDelivered || 0,
            amountDelivered: i.amountDelivered || 0,
            coDeltaQty: i.deltaQty || 0,
          },
          { budget: 1 },
        );
      } else if (i.source === "manual") {
        add(
          {
            itemCode: i.itemCode,
            description:
              i.costCodeDescription ||
              i.itemDescription ||
              i.description ||
              i.name ||
              "",
            uom: i.uom || i.container,
            coveragePerUnit: i.coveragePerUnit,
            netQty: i.requiredQty || 0,
            netCost: 0,
            qtyOrdered: i.qtyOrdered || 0,
            amountOrdered: i.amountOrdered || 0,
            qtyDelivered: i.qtyDelivered || 0,
            amountDelivered: i.amountDelivered || 0,
            coDeltaQty: 0,
          },
          { budget: 1 },
        );
      }
    }
    return m;
  }, [items]);

  // One render row per code on the order
  const rows = useMemo(() => {
    const out = [];
    orderByCode.forEach((o, k) => {
      const b = budgetByCode.get(k);
      const unit = o.isCov ? covLabel(o.uom) : o.uom || "EA";
      const sizeCount = o.sizes.size;
      if (b && (b.budgetQty > 0 || b.budgetCost > 0)) {
        const orderedOthers = Math.max(0, (b.orderedQty || 0) - o.qty);
        const remaining = (b.budgetQty || 0) - (b.orderedQty || 0);
        const blendedUnitCost =
          b.budgetQty > 0 ? b.budgetCost / b.budgetQty : 0;
        out.push({
          offBudget: false,
          itemCode: o.itemCode,
          description: o.description || b?.description || "",
          unit,
          sizeCount,
          isCov: o.isCov,
          budgetQty: b.budgetQty || 0,
          budgetCost: b.budgetCost || 0,
          blendedUnitCost,
          hasCO: (b.coDeltaQty || 0) !== 0,
          coDeltaQty: b.coDeltaQty || 0,
          orderedOthers,
          orderedAmount: b.orderedAmount || 0,
          thisOrderQty: o.qty,
          thisOrderAmount: o.amount,
          delivered: b.deliveredQty || 0,
          deliveredAmount: b.deliveredAmount || 0,
          remaining,
        });
      } else {
        out.push({
          offBudget: true,
          itemCode: o.itemCode,
          description: o.description || descriptionByCode.get(k) || "",
          unit,
          sizeCount,
          sizesArr: [...o.sizes],
          isCov: o.isCov,
          budgetQty: 0,
          budgetCost: 0,
          blendedUnitCost: o.qty ? o.amount / o.qty : 0,
          hasCO: false,
          coDeltaQty: 0,
          orderedOthers: 0,
          orderedAmount: 0,
          thisOrderQty: o.qty,
          thisOrderAmount: o.amount,
          delivered: 0,
          deliveredAmount: 0,
          remaining: -o.qty,
        });
      }
    });
    // Over-budget rows first, then alphabetical — surfaces problems immediately
    return out.sort((a, b) => {
      const aOver = a.remaining < 0 ? 0 : 1;
      const bOver = b.remaining < 0 ? 0 : 1;
      if (aOver !== bOver) return aOver - bOver;
      return (a.itemCode || "").localeCompare(b.itemCode || "");
    });
  }, [orderByCode, budgetByCode, descriptionByCode]);

  // Split budgeted vs off-budget so the summary reflects only budget-tracked
  // spend. Off-budget items get their own section + subtotal below.
  const budgetedRows = useMemo(() => rows.filter((r) => !r.offBudget), [rows]);
  const offBudgetRows = useMemo(() => rows.filter((r) => r.offBudget), [rows]);
  const overBudgetRows = useMemo(
    () => budgetedRows.filter((r) => r.remaining < 0),
    [budgetedRows],
  );

  // Summary totals — BUDGETED items only.
  const totals = useMemo(
    () =>
      budgetedRows.reduce(
        (a, r) => ({
          budget: a.budget + (r.budgetCost || 0),
          ordered: a.ordered + (r.orderedAmount || 0),
          delivered: a.delivered + (r.deliveredAmount || 0),
          thisOrder: a.thisOrder + (r.thisOrderAmount || 0),
        }),
        { budget: 0, ordered: 0, delivered: 0, thisOrder: 0 },
      ),
    [budgetedRows],
  );

  // Unbudgeted spend rolled up separately — never folded into budget math.
  const offBudgetTotal = useMemo(
    () => offBudgetRows.reduce((s, r) => s + (r.thisOrderAmount || 0), 0),
    [offBudgetRows],
  );

  const remainingDollars = totals.budget - totals.ordered;
  const isOverBudget = remainingDollars < 0;
  const anyOffBudget = offBudgetRows.length > 0;
  const grandTotalThisOrder = totals.thisOrder + offBudgetTotal;

  if (!poId) return null;

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {loading && (
          <span className="ml-auto text-[10px] text-yellow-500 animate-pulse">
            Loading budget…
          </span>
        )}
      </div>

      {error && <p className="px-5 py-3 text-xs text-red-400">{error}</p>}

      {!error && !loading && rows.length === 0 && (
        <p className="px-5 py-6 text-center text-xs text-gray-500">
          No priced items on this order yet.
        </p>
      )}

      {/* ── HERO: the one number that answers "are we over budget?" ─────── */}
      {!error && budgetedRows.length > 0 && (
        <div
          className={cls(
            "px-5 py-4 border-b border-gray-700/60",
            isOverBudget ? "bg-red-950/30" : "bg-gray-900/40",
          )}
        >
          <div className="flex items-end justify-between gap-4 flex-wrap mb-2">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                {isOverBudget ? "Over Budget By" : "Budget Remaining"}
              </p>
              <p
                className={cls(
                  "text-3xl font-bold font-mono leading-none",
                  isOverBudget ? "text-red-400" : "text-emerald-400",
                )}
              >
                {isOverBudget ? "-" : ""}${fmt(Math.abs(remainingDollars))}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500 mb-0.5">
                This order adds
              </p>
              <p className="text-base font-semibold font-mono text-orange-300">
                ${fmt(grandTotalThisOrder)}
              </p>
              {anyOffBudget && (
                <p className="text-[10px] text-red-400 mt-0.5">
                  incl. ${fmt(offBudgetTotal)} unbudgeted
                </p>
              )}
            </div>
          </div>
          <BudgetHeroBar budget={totals.budget} spent={totals.ordered} />
          <div className="flex items-center justify-between mt-1.5 text-[10px] text-gray-500 font-mono">
            <span>
              ${fmt(totals.ordered)} committed of ${fmt(totals.budget)} budget
            </span>
            <span>
              {totals.budget > 0
                ? `${Math.round((totals.ordered / totals.budget) * 100)}%`
                : "—"}
            </span>
          </div>
          {overBudgetRows.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-900/60 text-red-300 border border-red-700/40">
                {overBudgetRows.length} item
                {overBudgetRows.length !== 1 ? "s" : ""} over budget
              </span>
              <span className="text-[10px] text-gray-500">
                {overBudgetRows.map((r) => r.itemCode).join(", ")}
              </span>
            </div>
          )}
        </div>
      )}

      {!error && budgetedRows.length > 0 && (
        <>
          {/* Secondary context — smaller, below the hero, not competing with it */}
          <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-gray-700/50">
            {[
              {
                label: "Total Budget",
                value: totals.budget,
                color: "text-blue-300",
              },
              {
                label: "Committed So Far",
                value: totals.ordered,
                color: "text-yellow-300",
              },
              {
                label: "Delivered",
                value: totals.delivered,
                color: "text-green-300",
              },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="bg-gray-900/60 rounded-lg border border-gray-700 p-2.5"
              >
                <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">
                  {label}
                </p>
                <p className={cls("text-sm font-bold font-mono", color)}>
                  ${fmt(value)}
                </p>
              </div>
            ))}
          </div>

          {/* Per-code table toggle */}
          <button
            type="button"
            onClick={() => setShowTable((s) => !s)}
            className="w-full px-5 py-2 text-[11px] text-gray-400 hover:text-white bg-gray-900/30 hover:bg-gray-900/60 transition-colors flex items-center justify-between"
          >
            <span>
              Line-by-line breakdown ({budgetedRows.length} item
              {budgetedRows.length !== 1 ? "s" : ""})
            </span>
            <span className="text-gray-500">
              {showTable ? "▲ hide" : "▼ show"}
            </span>
          </button>

          {showTable && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-900">
                  <tr>
                    {[
                      ["Item", "text-left"],
                      ["Budget $", "text-right"],
                      ["Committed $", "text-right"],
                      ["This Order $", "text-right"],
                      ["Remaining $", "text-right"],
                      ["Qty Progress", "text-left min-w-[140px]"],
                    ].map(([h, a]) => (
                      <th
                        key={h}
                        className={cls(
                          "px-3 py-2 text-gray-400 font-medium",
                          a,
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {budgetedRows.map((row, idx) => {
                    const rowOver = row.remaining < 0;
                    return (
                      <tr
                        key={`${row.itemCode}-${idx}`}
                        className={cls(
                          rowOver
                            ? "bg-red-950/20"
                            : idx % 2 === 0
                              ? "bg-gray-800/30"
                              : "bg-gray-700/10",
                          "border-l-2",
                          rowOver ? "border-red-500" : "border-transparent",
                        )}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {row.description && (
                              <span className="text-xs text-gray-200 font-medium leading-snug">
                                {row.description}
                              </span>
                            )}
                            {rowOver && (
                              <span className="text-[9px] bg-red-700 text-white px-1.5 py-0.5 rounded font-bold">
                                OVER
                              </span>
                            )}
                            {row.hasCO && (
                              <span className="text-[9px] bg-purple-800/50 text-purple-300 px-1 py-0.5 rounded font-semibold">
                                CO {row.coDeltaQty > 0 ? "+" : ""}
                                {fmtQty(row.coDeltaQty)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="text-[10px] font-mono text-orange-400">
                              {row.itemCode || "—"}
                            </span>
                            <span className="text-[9px] text-gray-600">
                              {fmtQty(row.budgetQty)} {row.unit} budgeted
                              {row.sizeCount > 1
                                ? ` · ${row.sizeCount} sizes`
                                : ""}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-blue-300 font-mono">
                          ${fmt(row.budgetCost)}
                        </td>
                        <td className="px-3 py-2 text-right text-yellow-300 font-mono">
                          ${fmt(row.orderedAmount)}
                        </td>
                        <td className="px-3 py-2 text-right text-orange-300 font-mono font-semibold">
                          ${fmt(row.thisOrderAmount)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold">
                          <span
                            className={
                              rowOver ? "text-red-400" : "text-emerald-400"
                            }
                          >
                            {rowOver ? "-" : ""}$
                            {fmt(Math.abs(row.remaining * row.blendedUnitCost))}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <QtyBar
                            delivered={row.delivered}
                            ordered={row.orderedOthers + row.thisOrderQty}
                            estimated={row.budgetQty}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-900 border-t border-gray-600">
                  <tr>
                    <td className="px-3 py-2 text-gray-500">
                      {budgetedRows.length} item
                      {budgetedRows.length !== 1 ? "s" : ""}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-blue-300 font-mono">
                      ${fmt(totals.budget)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-yellow-300 font-mono">
                      ${fmt(totals.ordered)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-orange-300 font-mono">
                      ${fmt(totals.thisOrder)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold font-mono">
                      <span
                        className={
                          isOverBudget ? "text-red-400" : "text-emerald-400"
                        }
                      >
                        {isOverBudget ? "-" : ""}$
                        {fmt(Math.abs(remainingDollars))}
                      </span>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="px-5 py-2.5 flex items-center gap-5 text-[10px] text-gray-500 border-t border-gray-700/40 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />
              Delivered
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-600/60 inline-block" />
              Ordered (all MOs)
            </span>
            <span className="ml-auto text-gray-600">
              Grouped by item code, in dollars. "Remaining" = budget − committed
              across all MOs (this order included).
            </span>
          </div>
        </>
      )}

      {/* Off-budget items — tracked separately, NOT part of budget math above */}
      {!error && offBudgetRows.length > 0 && (
        <div className="border-t border-gray-700">
          <div className="px-5 py-3 border-b border-gray-700/50 flex items-center gap-3 flex-wrap bg-red-950/20">
            <h4 className="text-sm font-semibold text-red-300">
              Unbudgeted Items
            </h4>
            <span className="text-[10px] text-gray-500">
              not on this PO's budget — excluded from the numbers above
            </span>
            <span className="ml-auto text-xs font-mono font-bold text-red-300">
              ${fmt(offBudgetTotal)} this order
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900">
                <tr>
                  {[
                    ["Item", "text-left"],
                    ["Sizes", "text-center"],
                    ["UOM", "text-center"],
                    ["Qty", "text-right"],
                    ["Cost", "text-right"],
                  ].map(([h, a]) => (
                    <th
                      key={h}
                      className={cls("px-3 py-2 text-gray-400 font-medium", a)}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {offBudgetRows.map((row, idx) => (
                  <tr
                    key={`ob-${row.itemCode}-${idx}`}
                    className={cls(
                      idx % 2 === 0 ? "bg-gray-800/30" : "bg-gray-700/10",
                      "border-l-2 border-red-600/60",
                    )}
                  >
                    <td className="px-3 py-2">
                      {row.description && (
                        <span className="block text-xs text-gray-200 font-medium leading-snug">
                          {row.description}
                        </span>
                      )}
                      <span className="block text-[10px] font-mono text-orange-400 mt-0.5">
                        {row.itemCode || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center text-gray-300 font-mono">
                      {row.sizesArr && row.sizesArr.length > 0 ? (
                        row.sizesArr.join(", ")
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-300 uppercase">
                      {row.unit || "EA"}
                    </td>
                    <td className="px-3 py-2 text-right text-orange-300 font-mono font-semibold">
                      {fmtQty(row.thisOrderQty)}
                    </td>
                    <td className="px-3 py-2 text-right text-red-300 font-mono font-semibold">
                      ${fmt(row.thisOrderAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-900 border-t border-gray-600">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-gray-500">
                    {offBudgetRows.length} unbudgeted item
                    {offBudgetRows.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-red-300 font-mono">
                    ${fmt(offBudgetTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="px-5 py-2.5 text-[10px] text-gray-500 border-t border-gray-700/40">
            These item codes aren't on this PO's budget, so they have no
            remaining figure. Add them to the budget (or a change order) to
            track them against a target.
          </p>
        </div>
      )}
    </div>
  );
}
